import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Plus, Trash, Image, MusicNotes, X } from "@phosphor-icons/react";
import { SiteNav } from "../components/SiteNav";
import { ForgetMeNotIcon } from "../components/ForgetMeNotIcon";
import { api, formatApiErrorDetail, API } from "../lib/api";
import { chunkedUpload } from "../lib/chunkedUpload";
import { toast } from "sonner";

export default function ChapterEditor() {
    const { id, slug } = useParams();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [category, setCategory] = useState(null);
    const [memories, setMemories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [composerOpen, setComposerOpen] = useState(false);

    async function refresh() {
        try {
            const [p, ms, cats] = await Promise.all([
                api.get(`/profiles/${id}`),
                api.get(`/profiles/${id}/memories`, { params: { category: slug } }),
                api.get(`/categories`),
            ]);
            setProfile(p.data.profile);
            setMemories(ms.data.memories || []);
            setCategory((cats.data.categories || []).find((c) => c.slug === slug) || null);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load chapter.");
            navigate(`/profiles/${id}`);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, slug]);

    async function handleDeleteMemory(memId) {
        if (!confirm("Delete this memory?")) return;
        try {
            await api.delete(`/memories/${memId}`);
            setMemories((m) => m.filter((x) => x.id !== memId));
            toast.success("Memory removed.");
        } catch {
            toast.error("Could not delete.");
        }
    }

    if (loading || !profile || !category) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]" data-testid="chapter-loading">
                <ForgetMeNotIcon size={48} className="animate-pulse" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FDFBF7]" data-testid="chapter-page">
            <SiteNav />
            <main className="pt-28 pb-24 px-6 rem-watercolor-bg min-h-screen">
                <div className="max-w-5xl mx-auto">
                    <Link to={`/profiles/${id}`} className="inline-flex items-center gap-2 text-sm text-[#5C5A59] hover:text-[#6B7DB8] mb-8" data-testid="chapter-back">
                        <ArrowLeft size={16} /> Back to {profile.subject_name}'s remembr
                    </Link>

                    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                        <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-3">{category.group}</p>
                        <div className="flex items-end justify-between gap-4 flex-wrap">
                            <h1 className="font-serif text-4xl sm:text-5xl text-[#2D2B2A] leading-tight" data-testid="chapter-heading">
                                {category.label}
                            </h1>
                            <button
                                onClick={() => setComposerOpen(true)}
                                className="rem-start-button inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#6B7DB8] hover:bg-[#4A5A95] text-white font-medium text-sm transition-all"
                                data-testid="add-memory-btn"
                            >
                                <Plus size={16} weight="bold" /> Add a memory
                            </button>
                        </div>
                        <p className="mt-3 text-[#8A8786] text-sm">{memories.length} {memories.length === 1 ? "memory" : "memories"} in this chapter.</p>
                    </motion.div>

                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
                        {memories.map((m, i) => (
                            <motion.article
                                key={m.id}
                                initial={{ opacity: 0, y: 14 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: i * 0.04 }}
                                className="bg-white rounded-3xl border border-[#E5E1D8]/70 overflow-hidden shadow-rem-soft"
                                data-testid={`memory-${m.id}`}
                            >
                                {m.photo_file_id && (
                                    <AuthImg fileId={m.photo_file_id} alt={m.title} />
                                )}
                                <div className="p-6">
                                    <div className="flex justify-between items-start gap-2">
                                        <h3 className="font-serif text-xl text-[#2D2B2A] leading-snug">{m.title}</h3>
                                        <button onClick={() => handleDeleteMemory(m.id)} className="text-[#8A8786] hover:text-red-600 transition-colors" data-testid={`delete-memory-${m.id}`}>
                                            <Trash size={16} />
                                        </button>
                                    </div>
                                    {m.story && <p className="mt-3 text-[#5C5A59] leading-relaxed text-sm">{m.story}</p>}
                                    {m.audio_file_id && (
                                        <div className="mt-4 flex items-center gap-2 text-xs text-[#6B7DB8]">
                                            <MusicNotes size={14} weight="fill" />
                                            <AuthAudio fileId={m.audio_file_id} />
                                        </div>
                                    )}
                                    {(m.contributor_name || m.sort_year) && (
                                        <p className="mt-4 pt-3 border-t border-[#E5E1D8]/70 text-xs text-[#8A8786] italic">
                                            {m.contributor_name || ""}
                                            {m.contributor_name && m.sort_year ? " · " : ""}
                                            {m.sort_year || ""}
                                        </p>
                                    )}
                                </div>
                            </motion.article>
                        ))}
                    </div>

                    {memories.length === 0 && (
                        <div className="mt-12 rounded-3xl bg-white border border-[#E5E1D8]/60 p-12 text-center" data-testid="chapter-empty">
                            <Image size={36} weight="light" color="#8C9ECB" className="mx-auto mb-4" />
                            <h2 className="font-serif text-2xl text-[#2D2B2A]">No memories here yet</h2>
                            <p className="mt-3 text-[#5C5A59] max-w-md mx-auto">
                                Add the first one — a photo, a story, even a voice note from a grandchild. Every memory makes the lights switch on.
                            </p>
                            <button onClick={() => setComposerOpen(true)} className="rem-start-button mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#6B7DB8] hover:bg-[#4A5A95] text-white font-medium text-sm" data-testid="chapter-empty-add">
                                <Plus size={16} weight="bold" /> Add the first memory
                            </button>
                        </div>
                    )}
                </div>
            </main>

            {composerOpen && (
                <MemoryComposer
                    profileId={id}
                    categorySlug={slug}
                    onClose={() => setComposerOpen(false)}
                    onCreated={() => { setComposerOpen(false); refresh(); }}
                />
            )}
        </div>
    );
}

// --- Auth-protected image (fetched as blob since <img> can't send Authorization headers) ---
function AuthImg({ fileId, alt }) {
    const [src, setSrc] = useState(null);
    useEffect(() => {
        let url;
        let cancelled = false;
        (async () => {
            try {
                const resp = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
                if (cancelled) return;
                url = URL.createObjectURL(resp.data);
                setSrc(url);
            } catch { /* ignore */ }
        })();
        return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
    }, [fileId]);
    if (!src) return <div className="aspect-[4/3] bg-[#F4F6FB] animate-pulse" />;
    return <img src={src} alt={alt} className="w-full aspect-[4/3] object-cover" data-testid={`memory-img-${fileId}`} />;
}

function AuthAudio({ fileId }) {
    const [src, setSrc] = useState(null);
    useEffect(() => {
        let url;
        let cancelled = false;
        (async () => {
            try {
                const resp = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
                if (cancelled) return;
                url = URL.createObjectURL(resp.data);
                setSrc(url);
            } catch { /* ignore */ }
        })();
        return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
    }, [fileId]);
    if (!src) return <span>Loading audio…</span>;
    return <audio controls src={src} className="h-8" data-testid={`memory-audio-${fileId}`} />;
}

// --- Memory composer modal ---
function MemoryComposer({ profileId, categorySlug, onClose, onCreated }) {
    const [title, setTitle] = useState("");
    const [story, setStory] = useState("");
    const [contributor, setContributor] = useState("");
    const [year, setYear] = useState("");
    const [photoFile, setPhotoFile] = useState(null);
    // Photo album — first file becomes the cover (photo_file_id); rest become
    // photo_file_ids[] which the player rotates through every ~4s.
    const [albumFiles, setAlbumFiles] = useState([]);
    const [audioBlob, setAudioBlob] = useState(null);
    const [videoFile, setVideoFile] = useState(null);
    const [videoProgress, setVideoProgress] = useState(0);
    const [busy, setBusy] = useState(false);
    const [recording, setRecording] = useState(false);
    const [recorder, setRecorder] = useState(null);

    async function startRecording() {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mr = new MediaRecorder(stream);
            const chunks = [];
            mr.ondataavailable = (e) => chunks.push(e.data);
            mr.onstop = () => {
                const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
                setAudioBlob(blob);
                stream.getTracks().forEach((t) => t.stop());
                setRecording(false);
            };
            mr.start();
            setRecorder(mr);
            setRecording(true);
        } catch (e) {
            toast.error("Microphone access denied. Check browser permissions.");
        }
    }

    function stopRecording() {
        if (recorder && recorder.state !== "inactive") recorder.stop();
    }

    async function uploadBlob(blob, filename, contentType) {
        const fd = new FormData();
        fd.append("file", new File([blob], filename, { type: contentType }));
        const { data } = await api.post("/files/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return data.id;
    }

    async function submit(e) {
        e.preventDefault();
        if (!title.trim()) return;
        setBusy(true);
        try {
            let photo_file_id = null;
            let photo_file_ids = [];
            let audio_file_id = null;
            let video_file_id = null;
            if (photoFile) {
                photo_file_id = await uploadBlob(photoFile, photoFile.name, photoFile.type);
            }
            // Upload any extra album photos in parallel; preserve user order.
            if (albumFiles.length > 0) {
                const uploads = await Promise.all(
                    albumFiles.map((f) => uploadBlob(f, f.name, f.type))
                );
                // If no cover was picked separately, promote the first album photo
                // to be the cover and keep the remainder in photo_file_ids[].
                if (!photo_file_id) {
                    photo_file_id = uploads[0];
                    photo_file_ids = uploads.slice(1);
                } else {
                    photo_file_ids = uploads;
                }
            }
            if (audioBlob) {
                audio_file_id = await uploadBlob(audioBlob, "narration.webm", audioBlob.type || "audio/webm");
            }
            if (videoFile) {
                // Family videos can easily exceed proxy limits — always go via the
                // chunked uploader, even for small files. (Backend assembles ≤200MB.)
                video_file_id = await chunkedUpload(videoFile, {
                    onProgress: (p) => setVideoProgress(Math.round(p * 100)),
                });
            }
            await api.post(`/profiles/${profileId}/memories`, {
                category_slug: categorySlug,
                title: title.trim(),
                story: story.trim() || null,
                contributor_name: contributor.trim() || null,
                sort_year: year ? Number(year) : null,
                photo_file_id, photo_file_ids, audio_file_id, video_file_id,
            });
            toast.success("Memory added.");
            onCreated();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save memory.");
        } finally {
            setBusy(false);
            setVideoProgress(0);
        }
    }

    return (
        <div className="fixed inset-0 bg-[#2D2B2A]/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="memory-composer">
            <motion.div
                initial={{ opacity: 0, y: 24, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="bg-white rounded-[2rem] w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-rem-card"
            >
                <div className="p-6 sm:p-8">
                    <div className="flex justify-between items-start mb-6">
                        <div>
                            <p className="text-xs uppercase tracking-[0.22em] text-[#6B7DB8] font-semibold">New memory</p>
                            <h2 className="font-serif text-2xl text-[#2D2B2A] mt-1">Add what you remember</h2>
                        </div>
                        <button onClick={onClose} className="text-[#8A8786] hover:text-[#2D2B2A]" data-testid="composer-close"><X size={20} /></button>
                    </div>

                    <form onSubmit={submit} className="space-y-3" data-testid="composer-form">
                        <input required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title — e.g. The day we drove to Lake Eildon" className={inputCls} data-testid="composer-title" />
                        <textarea value={story} onChange={(e) => setStory(e.target.value)} rows={4} placeholder="Tell the story…" className={`${inputCls} rounded-2xl py-3`} data-testid="composer-story" />
                        <div className="grid grid-cols-2 gap-3">
                            <input value={contributor} onChange={(e) => setContributor(e.target.value)} placeholder="Who added this? (Issy)" className={inputCls} data-testid="composer-contributor" />
                            <input type="number" min={1900} max={2100} value={year} onChange={(e) => setYear(e.target.value)} placeholder="Year (1992)" className={inputCls} data-testid="composer-year" />
                        </div>

                        {/* Photo upload — cover (single) */}
                        <label className="block px-4 py-3 rounded-2xl border border-dashed border-[#8C9ECB]/40 bg-[#F4F6FB] text-sm text-[#5C5A59] cursor-pointer hover:bg-[#EDF0F8]" data-testid="composer-photo-label">
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => setPhotoFile(e.target.files?.[0] || null)} data-testid="composer-photo-input" />
                            {photoFile ? `📷 ${photoFile.name} (cover)` : "📷 Add a cover photo (optional)"}
                        </label>

                        {/* Photo album — extras that the player rotates through every ~4s */}
                        <label
                            className="block px-4 py-3 rounded-2xl border border-dashed border-[#A8B5E0]/40 bg-[#EDF0F8] text-sm text-[#5C5A59] cursor-pointer hover:bg-[#DDE5F2]"
                            data-testid="composer-album-label"
                        >
                            <input
                                type="file"
                                accept="image/*"
                                multiple
                                className="hidden"
                                onChange={(e) => setAlbumFiles(Array.from(e.target.files || []))}
                                data-testid="composer-album-input"
                            />
                            {albumFiles.length > 0
                                ? `🖼️ Album: ${albumFiles.length} photo${albumFiles.length === 1 ? "" : "s"} — rotates every 4s in the player`
                                : "🖼️ Add extra photos to make an album (optional · rotates automatically)"}
                        </label>

                        {/* Voice narration */}
                        <div className="px-4 py-3 rounded-2xl border border-dashed border-[#8C9ECB]/40 bg-[#F4F6FB] text-sm text-[#5C5A59]">
                            {audioBlob ? (
                                <div className="flex items-center justify-between gap-2">
                                    <span>🎙️ Narration recorded ({Math.round(audioBlob.size / 1024)} KB)</span>
                                    <button type="button" onClick={() => setAudioBlob(null)} className="text-xs text-red-600 hover:underline" data-testid="composer-audio-clear">remove</button>
                                </div>
                            ) : recording ? (
                                <button type="button" onClick={stopRecording} className="w-full text-red-600 font-medium" data-testid="composer-audio-stop">⏹ Stop recording</button>
                            ) : (
                                <button type="button" onClick={startRecording} className="w-full text-left" data-testid="composer-audio-start">
                                    🎙️ Record voice narration (optional)
                                </button>
                            )}
                        </div>

                        {/* Family video (chunked upload — supports MP4 / MOV / WebM up to 200MB) */}
                        <label
                            className="block px-4 py-3 rounded-2xl border border-dashed border-[#A85D62]/40 bg-[#FBF1F2] text-sm text-[#5C5A59] cursor-pointer hover:bg-[#F4E0E3]"
                            data-testid="composer-video-label"
                        >
                            <input
                                type="file"
                                accept="video/mp4,video/quicktime,video/webm"
                                className="hidden"
                                onChange={(e) => { setVideoFile(e.target.files?.[0] || null); setVideoProgress(0); }}
                                data-testid="composer-video-input"
                            />
                            {videoFile ? (
                                videoProgress > 0 && videoProgress < 100 ? (
                                    <span>📹 Uploading {videoFile.name} · {videoProgress}%</span>
                                ) : (
                                    <span>📹 {videoFile.name} ({Math.round(videoFile.size / 1024 / 1024)} MB)</span>
                                )
                            ) : (
                                "📹 Add a family video (optional · MP4 up to 200MB)"
                            )}
                        </label>

                        <button type="submit" disabled={busy || !title.trim()} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#6B7DB8] hover:bg-[#4A5A95] text-white font-medium text-sm transition-all disabled:opacity-60 mt-2" data-testid="composer-submit">
                            {busy ? "Saving…" : "Save memory"}
                        </button>
                    </form>
                </div>
            </motion.div>
        </div>
    );
}

const inputCls = "w-full px-5 py-3 rounded-full text-sm bg-white text-[#2D2B2A] placeholder-[#8A8786] border border-[#E5E1D8] focus:border-[#8C9ECB] outline-none transition-all";
